import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { stepsFrom, type TourStep } from './steps';
import './tour.css';

/* The onboarding tour.
 *
 * Hand-rolled rather than pulled from a library, for one reason that decided it: this tour
 * walks across ROUTES — the series list, a real series, the two banks — and the popular
 * tour libraries all assume a single page and lose their anchor the moment the router swaps the
 * DOM. Handling that ourselves is about eighty lines; fighting a library about it is more.
 *
 * It runs against REAL data, deliberately: a tour of an empty demo teaches you where buttons
 * are in a demo. It therefore has one hard rule — it may open panels and tick checkboxes so
 * there is something to point at, and it may NEVER create, edit or delete anything.
 *
 * When a target is missing (an empty series, a list with nothing in it) the step still shows,
 * centred, with the same words. A tour that dead-ends on a fresh account teaches nobody
 * anything, and a fresh account is exactly who is taking it.
 */

const WAIT_FOR_TARGET_MS = 2500;
const POLL_MS = 100;
/** Breathing room between the spotlight and the popover. */
const GAP = 14;
const CARD_W = 380;

interface Rect { top: number; left: number; width: number; height: number }

export default function Tour({ onClose, startAt }: { onClose: () => void; startAt?: string }) {
  const navigate = useNavigate();
  /* A deep link runs ONE chapter. `startAt` is a step id or a chapter name; anything it does not
     recognise falls back to the whole tour, because an unrecognised link opening an empty tour
     is the worst of the three outcomes. Resolved once — changing it mid-tour is not a thing. */
  const [steps] = useState<TourStep[]>(() => stepsFrom(startAt));
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState<Rect | null>(null);
  const [seriesId, setSeriesId] = useState<string | null>(null);
  const cancelled = useRef(false);

  const step: TourStep = steps[index];
  const last = index === steps.length - 1;

  /* Which series the editor steps open. A real one, so the tour shows real headlines — the
     first non-archived one, which is the one most likely being worked on. */
  useEffect(() => {
    cancelled.current = false;
    void (async () => {
      try {
        const rounds = await api.listRounds();
        const first = rounds.find((r) => r.status !== 'archived') || rounds[0];
        if (!cancelled.current) setSeriesId(first?.id || '');
      } catch {
        // No list, no editor steps to anchor — they fall back to centred cards.
        if (!cancelled.current) setSeriesId('');
      }
    })();
    return () => { cancelled.current = true; };
  }, []);

  /* Whether the tour — not the reader — expanded the intake explainer, so finish() can put it
     back. Collapsing it is a choice somebody made and the tour is a visitor here. */
  const tourExpandedExplainer = useRef(false);

  /** Open a panel or tick a box so the step has something to point at. Never writes. */
  const runSetUp = useCallback((kind: TourStep['setUp']) => {
    const explainerToggle = () =>
      document.querySelector('[data-testid="intake-explainer-toggle"]') as HTMLButtonElement | null;
    const clickByText = (text: string) => {
      const btn = [...document.querySelectorAll('.head-actions button')]
        .find((b) => b.textContent?.trim() === text) as HTMLButtonElement | undefined;
      btn?.click();
    };
    /* ⚠️ These MUST be idempotent. runSetUp is called on every poll tick — every 100ms until the
       target appears — and each of these buttons TOGGLES its panel. A panel whose contents are
       fetched takes a few hundred ms to render, so an unconditional click opened it, shut it,
       opened it… and the step reported its target missing. Only click when the panel that is
       already open is not the one we want. */
    const openPanel = () => document.querySelector('.panel h2')?.textContent?.trim() || '';
    const ensurePanel = (heading: string, button: string) => {
      if (openPanel() !== heading) clickByText(button);
    };
    if (kind === 'open-csv') ensurePanel('Import / Export', 'Import / Export');
    if (kind === 'open-history') ensurePanel('History', 'History');
    if (kind === 'open-trash') ensurePanel('Recently Deleted', 'Recently Deleted');
    if (kind === 'open-plays') ensurePanel('Plays', 'Plays');
    /* ⚠️ `close-panels` was in the setUp union from the start and never implemented, so a step
       using it silently did nothing. Nothing used it, so it never showed — until the panel steps
       above, which must not carry an open panel into the next chapter. Each button toggles, so
       closing is clicking whichever one is currently open. */
    if (kind === 'close-panels') {
      const open = openPanel();
      const label: Record<string, string> = {
        'Recently Deleted': 'Recently Deleted', Plays: 'Plays', History: 'History',
        'Import / Export': 'Import / Export', 'Play links': 'Share Link',
      };
      if (open && label[open]) clickByText(label[open]);
    }
    /* The Show only these step applies a real filter. The tour leaves the page as it found it,
       the same reason `unpick-all` exists. */
    if (kind === 'clear-filters') {
      const clear = [...document.querySelectorAll('.filter-bar button')]
        .find((b) => b.textContent?.trim() === 'Clear') as HTMLButtonElement | undefined;
      clear?.click();
    }
    if (kind === 'pick-first') {
      const box = document.querySelector('.item-pick') as HTMLInputElement | null;
      if (box && !box.checked) box.click();
    }
    if (kind === 'unpick-all') {
      document.querySelectorAll<HTMLInputElement>('.item-pick:checked').forEach((b) => b.click());
    }
    /* ⚠️ Idempotent by the aria-expanded guard, and it has to be: runSetUp fires every 100ms
       until the target appears, and this button TOGGLES — unguarded it would strobe the
       explainer open and shut for as long as the step is on screen. */
    if (kind === 'open-explainer') {
      const btn = explainerToggle();
      if (btn && btn.getAttribute('aria-expanded') === 'false') {
        btn.click();
        tourExpandedExplainer.current = true;
      }
    }
    if (kind === 'restore-explainer') {
      const btn = explainerToggle();
      if (tourExpandedExplainer.current && btn && btn.getAttribute('aria-expanded') === 'true') {
        btn.click();
      }
      tourExpandedExplainer.current = false;
    }
  }, []);

  const measure = useCallback((selector?: string) => {
    if (!selector) return setRect(null);
    const el = document.querySelector(selector);
    if (!el) return setRect(null);
    const b = el.getBoundingClientRect();
    setRect({ top: b.top, left: b.left, width: b.width, height: b.height });
  }, []);

  // Navigate if the step lives elsewhere, run its set-up, then wait for the target to appear.
  useEffect(() => {
    let stop = false;
    const path = typeof step.path === 'function' ? step.path(seriesId || '') : step.path;

    // Editor steps cannot run until we know which series to open.
    if (typeof step.path === 'function' && seriesId === null) return;

    if (path && window.location.pathname !== `${import.meta.env.BASE_URL}${path.replace(/^\//, '')}`) {
      navigate(path);
    }

    setRect(null);
    const started = Date.now();
    const tick = () => {
      if (stop) return;
      runSetUp(step.setUp);
      const el = step.target ? document.querySelector(step.target) : null;
      if (!step.target) return;
      if (el) {
        /* Scroll it into view before measuring, or the spotlight lands on a rect that is
           off-screen and the popover points at nothing. */
        el.scrollIntoView({ block: 'center', inline: 'nearest' });
        window.setTimeout(() => { if (!stop) measure(step.target); }, 120);
        return;
      }
      if (Date.now() - started < WAIT_FOR_TARGET_MS) window.setTimeout(tick, POLL_MS);
      // Otherwise: no target, and the step shows centred. Deliberately not an error.
    };
    const first = window.setTimeout(tick, 80);

    return () => { stop = true; window.clearTimeout(first); };
  }, [index, measure, navigate, runSetUp, seriesId, step]);

  // The page moves under a sticky bar and a resize changes everything; re-measure on both.
  useEffect(() => {
    const again = () => measure(step.target);
    window.addEventListener('resize', again);
    window.addEventListener('scroll', again, true);
    return () => {
      window.removeEventListener('resize', again);
      window.removeEventListener('scroll', again, true);
    };
  }, [measure, step.target]);

  const finish = useCallback(() => {
    /* Leave the page as we found it. A ticked checkbox, an applied filter and an open panel are
       all things the tour opened and none of them are its to leave behind. */
    runSetUp('unpick-all');
    runSetUp('clear-filters');
    runSetUp('close-panels');
    runSetUp('restore-explainer');
    onClose();
  }, [onClose, runSetUp]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') finish();
      if (e.key === 'ArrowRight') setIndex((i) => Math.min(i + 1, steps.length - 1));
      if (e.key === 'ArrowLeft') setIndex((i) => Math.max(i - 1, 0));
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    /* `steps.length` is listed even though it cannot change — the slice is resolved once in
       useState and TourContext keys this component by startAt, so a different run is a different
       mount. Declaring it anyway costs one re-subscribe that never happens, and stops the day
       `steps` becomes stateful from silently capturing a stale length. */
  }, [finish, steps.length]);

  /* Below the target when it fits, above when it fits there, and centred when neither does.
   *
   * ⚠️ Measured, not guessed. The first version placed the card with `translateY(-100%)` from
   * the target's top edge, which puts the card's BOTTOM at that line — so a target near the top
   * of the page pushed the card off the top of the screen entirely, Next included. It only
   * showed up against production's data, because where a target sits depends on how many
   * headlines are above it. Compute from the card's real height and clamp to the viewport. */
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    const card = cardRef.current;
    if (!card) return;
    const vh = window.innerHeight;
    const vw = window.innerWidth;
    const h = card.offsetHeight;
    const clampTop = (t: number) => Math.min(Math.max(12, t), Math.max(12, vh - h - 12));

    if (!rect) {
      setPos({ top: clampTop((vh - h) / 2), left: Math.max(12, (vw - CARD_W) / 2) });
      return;
    }
    const below = rect.top + rect.height + GAP;
    const above = rect.top - GAP - h;
    const top = below + h + 12 <= vh ? below : above >= 12 ? above : clampTop((vh - h) / 2);
    const left = Math.min(Math.max(12, rect.left + rect.width / 2 - CARD_W / 2), vw - CARD_W - 12);
    setPos({ top: clampTop(top), left });
  }, [rect, index]);

  // Hidden until measured, so the card is never painted at a wrong position first.
  const cardStyle: React.CSSProperties = pos
    ? { top: pos.top, left: pos.left }
    : { top: 0, left: 0, visibility: 'hidden' };

  return (
    <div className="tour" role="dialog" aria-label="Product tour" data-testid="tour">
      {/* The spotlight is one box-shadow: the hole is this div, everything outside it is the
          shadow. Cheaper and sharper than four backdrop panels meeting at the corners. */}
      {rect ? (
        <div
          className="tour-hole"
          style={{ top: rect.top - 6, left: rect.left - 6, width: rect.width + 12, height: rect.height + 12 }}
        />
      ) : (
        <div className="tour-hole tour-hole-none" />
      )}

      {/* Says whether this step CLAIMS a target, so verification can tell "deliberately centred"
          from "its selector no longer matches anything" — which is how a tour rots. */}
      <div
        ref={cardRef}
        className="tour-card"
        style={cardStyle}
        data-testid="tour-card"
        data-anchored={step.target ? 'true' : 'false'}
        /* For the tour's own test. `optional` steps describe things that may legitimately not be
           on the page — finish the outstanding artwork and the readiness banner is gone — so a
           missing target there is a fall-back to a centred card, not a broken step. Without this
           the test cannot tell that apart from a selector someone renamed. */
        data-optional={step.optional ? 'true' : 'false'}
        data-step-id={step.id || ''}
        data-chapter={step.chapter}
      >
        <div className="tour-step">Step {index + 1} of {steps.length}</div>
        <h2 className="tour-title">{step.title}</h2>
        <p className="tour-body">{step.body}</p>
        <div className="tour-actions">
          <button className="btn ghost" onClick={finish} data-testid="tour-skip">
            {last ? 'Close' : 'Skip'}
          </button>
          <span className="tour-spacer" />
          <button className="btn ghost" onClick={() => setIndex((i) => Math.max(i - 1, 0))} disabled={index === 0}>
            Back
          </button>
          {last ? (
            <button className="btn primary" onClick={finish} data-testid="tour-done">Done</button>
          ) : (
            <button className="btn primary" onClick={() => setIndex((i) => i + 1)} data-testid="tour-next">Next</button>
          )}
        </div>
      </div>
    </div>
  );
}
